import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/pages/admin/AdminShell.jsx";
import { Plus, Zap, Pause, Play, X, Paperclip } from "lucide-react";
import { AttachmentPicker } from "@/components/AttachmentPicker.jsx";

const TRIGGERS = [
  { k: "form_submitted", label: "Form submitted" },
  { k: "partial_older", label: "Partial older than X hours" },
  { k: "stage_changed", label: "Stage changed" },
  { k: "interview_booked", label: "Interview booked" },
  { k: "no_show", label: "No-show" },
  { k: "added_to_group", label: "Added to group" },
];
const STEP_KINDS = [
  { k: "send_wa", label: "Send WhatsApp" },
  { k: "send_email", label: "Send email" },
  { k: "wait", label: "Wait" },
  { k: "condition", label: "Condition on score/stage/reply" },
  { k: "move_stage", label: "Move stage" },
  { k: "add_tag", label: "Add tag" },
];

export default function Outreach() {
  const [tab, setTab] = useState("send");
  const [templates, setTemplates] = useState([]);
  const [chains, setChains] = useState([]);
  const [log, setLog] = useState([]);
  const [outreach, setOutreach] = useState([]);
  const [newTpl, setNewTpl] = useState(null);
  const [editAtt, setEditAtt] = useState(null);
  const [newChain, setNewChain] = useState(null);
  const [newOutreach, setNewOutreach] = useState(null);

  const load = async () => {
    const [t, c, l, o] = await Promise.all([
      api.get("/comms/templates"), api.get("/chains"),
      api.get("/comms/log"), api.get("/outreach"),
    ]);
    setTemplates(t.data); setChains(c.data); setLog(l.data); setOutreach(o.data);
  };
  useEffect(() => { load(); }, []);

  const saveTpl = async () => {
    if (!newTpl.name || !newTpl.body) return toast.warning("Name + body required");
    await api.post("/comms/templates", newTpl);
    toast.success("Template saved"); setNewTpl(null); load();
  };
  const saveAtt = async () => {
    try { await api.patch(`/comms/templates/${editAtt.template_id}`, { attachments: editAtt.attachments }); }
    catch (e) { return toast.error(e?.response?.data?.detail || "Couldn't save attachments"); }
    toast.success("Attachments saved"); setEditAtt(null); load();
  };
  const saveChain = async () => {
    if (!newChain.name) return toast.warning("Name required");
    const payload = { ...newChain };
    if (newChain.chain_id) await api.patch(`/chains/${newChain.chain_id}`, payload);
    else await api.post("/chains", payload);
    toast.success("Chain saved"); setNewChain(null); load();
  };
  const toggleChain = async (c) => {
    await api.patch(`/chains/${c.chain_id}`, { ...c, enabled: !c.enabled });
    load();
  };
  const addOutreach = async () => {
    if (!newOutreach.name) return toast.warning("Name required");
    await api.post("/outreach", newOutreach);
    setNewOutreach(null); load();
  };

  const watiOn = !!templates.find((t) => t.wati);

  return (
    <div>
      <PageHeader
        eyebrow="HIRING · OUTREACH"
        title="Outreach"
        subtitle={`WhatsApp (Wati${watiOn ? "" : " · mock"}) + email automations · ${outreach.length} manual leads`}
        stats={[
          { label: "Templates", value: templates.length },
          { label: "Chains", value: chains.length },
          { label: "Enabled", value: chains.filter((c) => c.enabled).length, accent: "pass" },
          { label: "Messages sent", value: log.length },
        ]}
      />

      <div className="flex gap-1 border-b border-[#1E1E1E] mb-5">
        {["send", "templates", "chains", "linkedin"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t ? "border-[#F4F4F5] text-[#F4F4F5]" : "border-transparent text-[#71717A] hover:text-[#F4F4F5]"}`}
            data-testid={`outreach-tab-${t}`}>
            {t === "linkedin" ? "LinkedIn tracker" : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === "send" && (
        <div className="tt-card overflow-hidden">
          <div className="px-4 py-3 border-b border-[#1E1E1E] text-sm text-[#A1A1AA]">
            Pick candidates in <a className="text-[#5B9BFF]" href="/admin/candidates">Candidates</a>, then "Send comms". Recent log below.
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#0F0F0F]"><tr className="text-[#71717A] text-[10px] font-mono tracking-wider uppercase">
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Channel</th>
              <th className="text-left px-4 py-2">To</th>
              <th className="text-left px-4 py-2">Body</th>
              <th className="text-left px-4 py-2">Status</th>
            </tr></thead>
            <tbody>
              {log.map((r) => (
                <tr key={r.log_id} className="border-t border-[#1E1E1E]">
                  <td className="px-4 py-3 font-mono text-xs">{new Date(r.sent_at).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className="tt-badge-info">{r.channel}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{r.to}</td>
                  <td className="px-4 py-3 text-[#A1A1AA] truncate max-w-sm">{r.body}</td>
                  <td className="px-4 py-3"><span className="tt-badge-neutral">{r.provider_status || r.status}</span></td>
                </tr>
              ))}
              {log.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#71717A]">No messages sent yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "templates" && (
        <>
          <div className="flex justify-end mb-3"><button onClick={() => setNewTpl({ name: "", channel: "whatsapp", subject: "", body: "Hi {{first_name}}, …", wati_template_name: "", attachments: [] })} className="tt-btn-primary text-sm flex items-center gap-2"><Plus size={14} /> New template</button></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.filter((t) => !t.wati).map((t) => (
              <div key={t.template_id || t.name} className="tt-card p-4">
                <div className="flex justify-between items-start mb-1">
                  <div className="font-medium">{t.name}</div>
                  <span className="tt-badge-info">{t.channel}</span>
                </div>
                {t.wati_template_name && <div className="text-xs font-mono text-[#71717A] mb-1">wati: {t.wati_template_name}</div>}
                <div className="text-sm text-[#A1A1AA] whitespace-pre-wrap">{t.body}</div>
                <div className="flex flex-wrap items-center gap-1.5 mt-3">
                  {(t.attachments || []).map((a) => <span key={a.file_id} className="tt-badge-neutral flex items-center gap-1 !normal-case"><Paperclip size={11} /> {a.original_name}</span>)}
                  <button onClick={() => setEditAtt({ template_id: t.template_id, name: t.name, attachments: t.attachments || [] })} className="tt-btn-ghost text-xs flex items-center gap-1" data-testid={`template-attachments-${t.template_id}`}><Paperclip size={12} /> {(t.attachments || []).length ? "Edit files" : "Attach files"}</button>
                </div>
              </div>
            ))}
            {templates.filter((t) => !t.wati).length === 0 && <div className="text-sm text-[#71717A]">No templates yet. {watiOn ? "Approved Wati templates will appear automatically." : "Set WATI_API_TOKEN to pull approved WhatsApp templates."}</div>}
          </div>
        </>
      )}

      {tab === "chains" && (
        <>
          <div className="flex justify-end mb-3"><button onClick={() => setNewChain({ name: "", trigger: { kind: "form_submitted" }, steps: [], enabled: false })} className="tt-btn-primary text-sm flex items-center gap-2"><Plus size={14} /> New chain</button></div>
          <div className="space-y-3">
            {chains.map((c) => (
              <div key={c.chain_id} className="tt-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="cursor-pointer" onClick={() => setNewChain({ ...c })}>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs font-mono text-[#71717A]">trigger: {c.trigger.kind} · {c.steps.length} steps · enrolled {c.enrolled}</div>
                  </div>
                  <button onClick={() => toggleChain(c)} className={`tt-btn-secondary text-xs flex items-center gap-1 ${c.enabled ? "border-[#22C55E] text-[#22C55E]" : ""}`}>
                    {c.enabled ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Enable</>}
                  </button>
                </div>
                <div className="flex gap-1 flex-wrap text-xs">
                  {c.steps.map((s, i) => <span key={i} className="tt-badge-neutral">{i+1}. {s.kind}</span>)}
                </div>
              </div>
            ))}
            {chains.length === 0 && <div className="text-sm text-[#71717A]">No chains yet.</div>}
          </div>
        </>
      )}

      {tab === "linkedin" && (
        <>
          <div className="flex justify-end mb-3"><button onClick={() => setNewOutreach({ name: "", profile_url: "", role_target: "", status: "To contact" })} className="tt-btn-primary text-sm flex items-center gap-2"><Plus size={14} /> Add contact</button></div>
          <div className="tt-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0F0F0F]"><tr className="text-[#71717A] text-[10px] font-mono tracking-wider uppercase"><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Role</th><th className="text-left px-4 py-2">Profile</th><th className="text-left px-4 py-2">Status</th></tr></thead>
              <tbody>
                {outreach.map((r) => (
                  <tr key={r.contact_id} className="border-t border-[#1E1E1E]">
                    <td className="px-4 py-3">{r.name}</td>
                    <td className="px-4 py-3">{r.role_target || "—"}</td>
                    <td className="px-4 py-3">{r.profile_url ? <a href={r.profile_url} target="_blank" rel="noreferrer" className="text-[#5B9BFF] font-mono text-xs truncate">{r.profile_url}</a> : "—"}</td>
                    <td className="px-4 py-3"><span className="tt-badge-neutral">{r.status}</span></td>
                  </tr>
                ))}
                {outreach.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-[#71717A]">No LinkedIn contacts yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modals */}
      {newTpl && (
        <Modal onClose={() => setNewTpl(null)} title="New template">
          <input className="tt-input mb-3" placeholder="Name" value={newTpl.name} onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })} />
          <select className="tt-input mb-3" value={newTpl.channel} onChange={(e) => setNewTpl({ ...newTpl, channel: e.target.value })}>
            <option value="whatsapp">WhatsApp</option><option value="email">Email</option>
          </select>
          {newTpl.channel === "email" && <input className="tt-input mb-3" placeholder="Subject" value={newTpl.subject} onChange={(e) => setNewTpl({ ...newTpl, subject: e.target.value })} />}
          {newTpl.channel === "whatsapp" && <input className="tt-input mb-3" placeholder="Wati approved template name (optional)" value={newTpl.wati_template_name} onChange={(e) => setNewTpl({ ...newTpl, wati_template_name: e.target.value })} />}
          <textarea rows={5} className="tt-input mb-4 font-mono text-xs" value={newTpl.body} onChange={(e) => setNewTpl({ ...newTpl, body: e.target.value })} />
          <AttachmentPicker value={newTpl.attachments || []} onChange={(attachments) => setNewTpl({ ...newTpl, attachments })} testId="new-template-attachments" />
          <div className="flex justify-end gap-2"><button onClick={() => setNewTpl(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={saveTpl} className="tt-btn-primary text-sm">Save</button></div>
        </Modal>
      )}
      {editAtt && (
        <Modal onClose={() => setEditAtt(null)} title={`Files · ${editAtt.name}`}>
          <p className="text-xs text-[#71717A] mb-3">These files are attached automatically whenever this template is used.</p>
          <AttachmentPicker value={editAtt.attachments} onChange={(attachments) => setEditAtt({ ...editAtt, attachments })} testId="edit-template-attachments" />
          <div className="flex justify-end gap-2"><button onClick={() => setEditAtt(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={saveAtt} className="tt-btn-primary text-sm" data-testid="save-template-attachments">Save</button></div>
        </Modal>
      )}
      {newChain && (
        <Modal onClose={() => setNewChain(null)} title={newChain.chain_id ? "Edit chain" : "New chain"}>
          <input className="tt-input mb-3" placeholder="Chain name" value={newChain.name} onChange={(e) => setNewChain({ ...newChain, name: e.target.value })} />
          <div className="tt-label mb-1">Trigger</div>
          <select className="tt-input mb-3" value={newChain.trigger.kind} onChange={(e) => setNewChain({ ...newChain, trigger: { ...newChain.trigger, kind: e.target.value } })}>
            {TRIGGERS.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
          </select>
          {newChain.trigger.kind === "partial_older" && (
            <input type="number" className="tt-input mb-3 font-mono" placeholder="Hours" value={newChain.trigger.hours || 24} onChange={(e) => setNewChain({ ...newChain, trigger: { ...newChain.trigger, hours: parseInt(e.target.value || "24") } })} />
          )}
          <div className="tt-label mb-1">Steps</div>
          <div className="space-y-2 mb-3">
            {newChain.steps.map((s, i) => (
              <div key={i} className="flex gap-2 items-center">
                <span className="font-mono text-xs text-[#71717A] w-6">{i+1}</span>
                <select className="tt-input !w-auto flex-1" value={s.kind} onChange={(e) => { const n = [...newChain.steps]; n[i] = { ...s, kind: e.target.value }; setNewChain({ ...newChain, steps: n }); }}>
                  {STEP_KINDS.map((k) => <option key={k.k} value={k.k}>{k.label}</option>)}
                </select>
                {s.kind === "wait" && <input type="number" className="tt-input !w-24 font-mono" placeholder="hrs" value={s.hours || 0} onChange={(e) => { const n = [...newChain.steps]; n[i] = { ...s, hours: parseInt(e.target.value || 0) }; setNewChain({ ...newChain, steps: n }); }} />}
                {(s.kind === "move_stage" || s.kind === "add_tag") && <input className="tt-input !w-40" placeholder={s.kind === "move_stage" ? "stage" : "tag"} value={s.value || ""} onChange={(e) => { const n = [...newChain.steps]; n[i] = { ...s, value: e.target.value }; setNewChain({ ...newChain, steps: n }); }} />}
                <button onClick={() => setNewChain({ ...newChain, steps: newChain.steps.filter((_, j) => j !== i) })} className="tt-btn-ghost text-xs"><X size={12} /></button>
              </div>
            ))}
            <button onClick={() => setNewChain({ ...newChain, steps: [...newChain.steps, { kind: "wait", hours: 24 }] })} className="tt-btn-secondary text-xs flex items-center gap-1"><Plus size={12} /> Add step</button>
          </div>
          <div className="flex justify-end gap-2"><button onClick={() => setNewChain(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={saveChain} className="tt-btn-primary text-sm">Save</button></div>
        </Modal>
      )}
      {newOutreach && (
        <Modal onClose={() => setNewOutreach(null)} title="LinkedIn contact">
          <input className="tt-input mb-3" placeholder="Name" value={newOutreach.name} onChange={(e) => setNewOutreach({ ...newOutreach, name: e.target.value })} />
          <input className="tt-input mb-3" placeholder="Profile URL" value={newOutreach.profile_url} onChange={(e) => setNewOutreach({ ...newOutreach, profile_url: e.target.value })} />
          <input className="tt-input mb-3" placeholder="Role target" value={newOutreach.role_target} onChange={(e) => setNewOutreach({ ...newOutreach, role_target: e.target.value })} />
          <div className="flex justify-end gap-2"><button onClick={() => setNewOutreach(null)} className="tt-btn-secondary text-sm">Cancel</button><button onClick={addOutreach} className="tt-btn-primary text-sm">Add</button></div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex items-center justify-center z-40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg tt-card p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">{title}</h2>{children}
      </div>
    </div>
  );
}
